
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Puzzle } from "lucide-react";
import { PublicActivityCard } from "./PublicActivityCard";
import { PaginationControls } from "./PaginationControls";
import { Tables } from "@/integrations/supabase/types";
import { ReactNode } from "react";

type Activity = Tables<"activities"> & {
  activity_categories?: {
    id: string;
    name: string;
    color: string | null;
    icon: string | null;
  } | null;
};

interface PrintableActivitiesSectionProps {
  loading: boolean;
  paginatedActivities: Activity[];
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  onResetFilters: () => void;
  searchComponent?: ReactNode;
  filtersComponent?: ReactNode;
}

export const PrintableActivitiesSection = ({
  loading,
  paginatedActivities,
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  onResetFilters,
  searchComponent,
  filtersComponent,
}: PrintableActivitiesSectionProps) => {
  return (
    <section className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-teedee-blue mb-4">Printable Activities</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Download and print these fun activities to enjoy with your family at home or in the classroom!
          </p>
        </div>

        {!loading && (searchComponent || filtersComponent) && (
          <div className="mb-12 space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:gap-8">
              {searchComponent && (
                <div className="lg:w-80">
                  {searchComponent}
                </div>
              )}
              {filtersComponent && (
                <div className="flex-1">
                  {filtersComponent}
                </div>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, index) => (
              <Card key={index} className="overflow-hidden shadow-xl bg-white/80 backdrop-blur-sm animate-pulse">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                    <div className="w-20 h-6 bg-gray-200 rounded-full"></div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-4"></div>
                  <div className="space-y-2 mb-6">
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded"></div>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 h-10 bg-gray-200 rounded"></div>
                    <div className="w-10 h-10 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : paginatedActivities.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {paginatedActivities.map((activity) => (
                <PublicActivityCard key={activity.id} activity={activity} />
              ))}
            </div>

            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
              onPageChange={onPageChange}
              onItemsPerPageChange={onItemsPerPageChange}
            />
          </>
        ) : (
          <div className="text-center py-12">
            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
              <Puzzle className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No activities found</h3>
            <p className="text-gray-600 mb-4">
              Try adjusting your search terms or filters to find what you're looking for.
            </p>
            <Button onClick={onResetFilters} variant="outline">
              Reset Filters
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};
